// Hooks de estado para el módulo quotes — sin react-query (no está en deps).
// Patrón espejo de modules/expo-capture/hooks/useExpoCaptures.ts.

import { useCallback, useEffect, useState } from 'react';

import { quotesApi, QuotesApiError } from '../api';
import type {
  Quote,
  QuoteCreateIn,
  QuoteListFilters,
  QuoteListResponse,
  QuoteTemplate,
  SendQuoteResponse,
} from '../types';

function toMessage(e: unknown): string {
  if (e instanceof QuotesApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

export interface UseQuotesResult {
  data: QuoteListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useQuotes(filters: QuoteListFilters = {}): UseQuotesResult {
  const [data, setData] = useState<QuoteListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filterKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await quotesApi.list(filters);
      setData(res);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export interface UseCreateQuoteResult {
  submit: (data: QuoteCreateIn) => Promise<Quote | null>;
  loading: boolean;
  error: string | null;
}

export function useCreateQuote(): UseCreateQuoteResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(async (data: QuoteCreateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await quotesApi.create(data);
    } catch (e) {
      setError(toMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading, error };
}

export interface UseSendQuoteResult {
  send: (id: string) => Promise<SendQuoteResponse | null>;
  loading: boolean;
  error: string | null;
}

export function useSendQuote(): UseSendQuoteResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      return await quotesApi.send(id);
    } catch (e) {
      setError(toMessage(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return { send, loading, error };
}

export interface UseQuoteTemplatesResult {
  data: QuoteTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useQuoteTemplates(): UseQuoteTemplatesResult {
  const [data, setData] = useState<QuoteTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await quotesApi.listTemplates();
      setData(res);
    } catch (e) {
      setError(toMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}
